/**
 * @file ClientSummaryTab.js
 * @description This file contains the ClientSummaryTab component, which displays a summary of the client in a three column layout.
 */

import React, { useState, useEffect } from 'react';
import { getDashboardData } from '../../services/dashboardService';
import { getClientOptions } from '../../services/clientService';
import { isSameYear } from 'date-fns';
import { searchPlanYears } from '../../services/planYearService';
import { getImportLastRunDates } from '../../services/importService';
import { format, parseISO } from 'date-fns';
import { DashboardTypeEnum, ClientOptionEnum, ImportType } from '../../utils/data/enums';
import { getTenantTierLabel, isValidUrl } from '../../utils/utils';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import './ClientSummaryTab.scss';
import TenantTierBadge from '../ui/svg/TenantTierBadge';
import { getText } from '../../i18n';

const initialClientSummaryLoadingState = {
    planYearLoaded: false,
    individualCountsLoaded: false,
    clientRegistrationIdentifiersLoaded: false,
    lastImportDataLoaded: false,
};

const ClientSummaryTab = ({ individualId, individualRecord }) => {
    const [loadingState, setLoadingState] = useState(
        initialClientSummaryLoadingState,
    );
    const [isLoading, setIsLoading] = useState(true);
    const [accountManager, setAccountManager] = useState(null);
    const [planYear, setPlanYear] = useState(null);
    const [individualCounts, setIndividualCounts] = useState({
        active: 0,
        inactive: 0,
    });
    const [registrationIdentifiers, setRegistrationIdentifiers] = useState([]);
    const [importData, setImportData] = useState({
        lastImportedDemoDate: null,
        lastImportedEnrollDate: null,
    });

    // Used to determine if the Client has a valid website.
    const isValidWebsite =
        individualRecord?.website && isValidUrl(individualRecord?.website);
    // Temporary text to be used on this Client Summary screen while we implement these features (Contract Begin/End date, Languages, Add-Ons)
    const comingSoonText = getText('msg_eaee1c01b01f');

    // Fetches the Plan Year for the client.
    const getPlanYear = async () => {
        try {
            const planYears = await searchPlanYears(individualId);
            const currentPlanYear = planYears?.viewModels?.find(planYear => {
                const start = parseISO(planYear.startDate);
                const end = parseISO(planYear.endDate);
                return isSameYear(start, end);
            });
            if (currentPlanYear) {
                const { code, startDate, endDate } = currentPlanYear;
                const formattedStartDate = format(
                    parseISO(startDate),
                    'MMM dd yyyy',
                );
                const formattedEndDate = format(
                    parseISO(endDate),
                    'MMM dd yyyy',
                );
                const formattedCurrentPlanYear = `${code} (${formattedStartDate} - ${formattedEndDate})`;
                setPlanYear(formattedCurrentPlanYear);
            } else {
                setPlanYear('-');
            }
        } catch (error) {
            Toast.error(getText('msg_5d234eaeb704'));
            console.error('Error fetching plan years.', error);
        }
        setLoadingState(prev => ({
            ...prev,
            planYearLoaded: true,
        }));
    };

    // Fetches the Registration Identifiers for the client.
    const getClientRegistrationIdentifiers = async () => {
        try {
            const clientOptions = await getClientOptions(individualId, [
                ClientOptionEnum.RegistrationIdentifiers,
            ]);
            const registrationOption = clientOptions?.find(
                option => option.name === 'Registration Identifiers',
            );
            if (
                registrationOption &&
                Array.isArray(registrationOption.values) &&
                Array.isArray(registrationOption.selectList)
            ) {
                const selectedLabels = registrationOption.values
                    .map(
                        valueId =>
                            registrationOption.selectList.find(
                                item => item.id === valueId,
                            )?.label,
                    )
                    .filter(label => typeof label === 'string');
                setRegistrationIdentifiers(selectedLabels);
            } else {
                setRegistrationIdentifiers([]);
            }
        } catch (err) {
            Toast.error(getText('msg_c47d1abff70d'));
            // eslint-disable-next-line no-console
            console.error('Error fetching client options: ', err);
        }
        setLoadingState(prev => ({
            ...prev,
            clientRegistrationIdentifiersLoaded: true,
        }));
    };

    // Fetches the Last Imported Demo and Last Imported Enroll for the client.
    const getLastImportData = async () => {
        try {
            // Helper to find the last import dates
            const findImportLastRunDates = imports => {
                // Find all Demo imports
                const demoImports = imports
                    .filter(
                        item =>
                            item.importTypeId === ImportType.Demographic
                    )
                    .map(item => item.lastRunDate);

                // Find all Enrollment imports
                const enrollImports = imports
                    .filter(
                        item =>
                            item.importTypeId === ImportType.Enrollment
                    )
                    .map(item => item.lastRunDate);
                const lastImportedDemoDate =
                    demoImports.length > 0
                        ? format(
                            parseISO(demoImports.sort().reverse()[0]),
                            'MM-dd-yyyy',
                        )
                        : null;

                const lastImportedEnrollDate =
                    enrollImports.length > 0
                        ? format(
                            parseISO(enrollImports.sort().reverse()[0]),
                            'MM-dd-yyyy',
                        )
                        : null;

                return { lastImportedDemoDate, lastImportedEnrollDate };
            };

            const imports = await getImportLastRunDates(individualId);
            const { lastImportedDemoDate, lastImportedEnrollDate } =
                findImportLastRunDates(imports);
            setImportData({ lastImportedDemoDate, lastImportedEnrollDate });
        } catch (err) {
            Toast.error(getText('msg_d8eab0942a8c'));
            // eslint-disable-next-line no-console
            console.error('Error fetching import data: ', err);
        }
        setLoadingState(prev => ({
            ...prev,
            lastImportDataLoaded: true,
        }));
    };

    // Fetches the Individual Counts for both Active and Inactive for the client.
    const getIndividualCounts = async () => {
        try {
            const createdAtDate = format(
                new Date(individualRecord?.createdAt),
                'yyyy-MM-dd',
            );
            const today = format(new Date(), 'yyyy-MM-dd');
            const allIndividualsPopulationGroups = [1];
            const employmentStatusData = await getDashboardData(
                individualId,
                createdAtDate,
                today,
                allIndividualsPopulationGroups,
                DashboardTypeEnum.EmploymentStatus,
            );
            // Set the Active count based off the Employment Status data active counts
            // Set the Inactive count based off all other Employment Status data counts
            if (Array.isArray(employmentStatusData)) {
                const activeObj = employmentStatusData.find(
                    item => item.employmentStatus === 'Active',
                );
                const activeCount = activeObj ? activeObj.count : 0;
                const inactiveCount = employmentStatusData
                    .filter(item => item.employmentStatus !== 'Active')
                    .reduce((sum, item) => sum + item.count, 0);
                setIndividualCounts({
                    active: activeCount,
                    inactive: inactiveCount,
                });
            }
        } catch (err) {
            Toast.error(getText('msg_c5bfd903efa6'));
            console.error('Error fetching individual counts: ', err);
        }
        setLoadingState(prev => ({
            ...prev,
            individualCountsLoaded: true,
        }));
    };

    // Finds and formats the account manager from the contacts array.
    const getAndFormatAccountManager = () => {
        const contacts = individualRecord?.contacts;
        if (!Array.isArray(contacts) || contacts.length === 0) {
            setAccountManager('-');
            return;
        }
        const accountManagers = contacts.filter(
            contact => contact.contactRole === 'Account Manager',
        );
        if (accountManagers.length === 0) {
            setAccountManager('-');
            return;
        }
        const mainAccountManager = accountManagers.find(
            contact => contact.isMain,
        );
        const resolved = mainAccountManager ?? accountManagers[0];
        const firstName = resolved.firstName?.trim();
        const lastName = resolved.lastName?.trim();
        setAccountManager(`${firstName} ${lastName}`.trim() || '-');
    };

    // Call all the fetch APIs on initial load, and set loadingState to false after all the data has loaded.
    useEffect(() => {
        getPlanYear();
        getIndividualCounts();
        getLastImportData();
        getClientRegistrationIdentifiers();
        getAndFormatAccountManager();
    }, []);

    useEffect(() => {
        if (
            loadingState.planYearLoaded === true &&
            loadingState.individualCountsLoaded === true &&
            loadingState.clientRegistrationIdentifiersLoaded === true &&
            loadingState.lastImportDataLoaded === true
        ) {
            setIsLoading(false);
        }
    }, [loadingState]);

    return isLoading ? (
        <Spinner width={50} className="client-summary-spinner" />
    ) : (
        <div className="client-summary-tab">
            <div className="client-summary-content">
                <div className="client-summary-column">
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label">{getText('msg_4fb43108e78f')}</span>
                        <span className="client-summary-value">
                            {individualRecord?.name}
                        </span>
                    </div>
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label">
                            {getText('msg_47ee869aaf33')}
                        </span>
                        <span className="client-summary-value">
                            {accountManager}
                        </span>
                    </div>
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label not-yet-implemented">
                            {getText('msg_72244055e282')}
                        </span>
                        <span className="client-summary-value not-yet-implemented">
                            {comingSoonText}
                        </span>
                    </div>
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label">
                            {getText('msg_d585301a9a20')}
                        </span>
                        <span className="client-summary-value">
                            {importData.lastImportedDemoDate || '-'}
                        </span>
                    </div>
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label">
                            {getText('msg_e8c34e02375e')}
                        </span>
                        {registrationIdentifiers.length > 0 ? (
                            registrationIdentifiers.map(identifier => (
                                <span
                                    className="client-summary-value"
                                    key={identifier}
                                >
                                    {identifier}
                                </span>
                            ))
                        ) : (
                            <span className="client-summary-value">{getText('msg_6eef6648406c')}</span>
                        )}
                    </div>
                </div>
                <div className="client-summary-column">
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label">{getText('msg_5c47e26c6ad2')}</span>
                        <span className="client-summary-value">
                            {individualRecord?.agency?.name}
                        </span>
                    </div>
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label">
                            {getText('msg_1020312b6175')}
                        </span>
                        <span className="client-summary-value">
                            {planYear || '-'}
                        </span>
                    </div>
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label not-yet-implemented">
                            {getText('msg_59cadefc05e4')}
                        </span>
                        <span className="client-summary-value not-yet-implemented">
                            {comingSoonText}
                        </span>
                    </div>
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label">
                            {getText('msg_77c52dd11e20')}
                        </span>
                        <span className="client-summary-value">
                            {importData.lastImportedEnrollDate || '-'}
                        </span>
                    </div>
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label not-yet-implemented">
                            {getText('msg_53c8dcd8a819')}
                        </span>
                        <span className="client-summary-value not-yet-implemented">
                            {comingSoonText}
                        </span>
                    </div>
                </div>
                <div className="client-summary-column">
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label">{getText('msg_5bd44ebe63cd')}</span>
                        <span className="client-summary-value">
                            <TenantTierBadge tenantTierId={individualRecord?.tenantTierId} size={24} />
                        </span>
                    </div>
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label">{getText('msg_2e8a57cc5c47')}</span>
                        <a
                            href={
                                isValidWebsite
                                    ? individualRecord?.website
                                    : null
                            }
                            className={`client-summary-value client-summary-link ${isValidWebsite ? 'client-summary-valid-link' : ''} `}
                        >
                            {isValidWebsite ? individualRecord?.website : '-'}
                        </a>
                    </div>
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label">
                            {getText('msg_9cd0c534cfbf')}
                        </span>
                        <span className="client-summary-counts-value-label-container">
                            <span className="client-summary-value">
                                {getText('msg_27120ee112bc')}
                            </span>
                            <span className="client-summary-right-aligned-value">
                                {individualCounts?.active}
                            </span>
                        </span>
                        <span className="client-summary-counts-value-label-container">
                            <span className="client-summary-value">
                                {getText('msg_a340e710366f')}
                            </span>
                            <span className="client-summary-right-aligned-value">
                                {individualCounts?.inactive}
                            </span>
                        </span>
                    </div>
                    <div className="client-summary-value-label-container">
                        <span className="client-summary-label not-yet-implemented">
                            {getText('msg_8cdf29157d5d')}
                        </span>
                        <span className="client-summary-value not-yet-implemented">
                            {comingSoonText}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientSummaryTab;
